import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { RescheduleMaintenanceRequest } from '../../../models/maintenance-overview-request.model';
import { MaintenanceOverviewService } from '../../../services/maintenance-overview.service';
import { ToastrService } from 'ngx-toastr';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceDetailsResponse } from '../../../models/maintenance-overview-response.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-reschedule-maintenance-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './reschedule-maintenance-modal.component.html',
  styleUrl: './reschedule-maintenance-modal.component.scss'
})
export class RescheduleMaintenanceModalComponent implements OnInit {
  @Input() maintenanceData!: MaintenanceDetailsResponse;

  rescheduleForm!: FormGroup;
  isLoading: boolean = false;
  dateValue: string = '';
  initialDateValue: string = '';

  private fb = inject(FormBuilder);
  public activeModal = inject(NgbActiveModal);
  private maintenanceService = inject(MaintenanceOverviewService);
  private toastr = inject(ToastrService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    const currentDate = this.maintenanceData?.dueDate
      ? new Date(this.maintenanceData.dueDate)
      : new Date();

    this.dateValue = this.formatDate(currentDate);
    this.initialDateValue = this.dateValue;

    this.rescheduleForm = this.fb.group({
      date: [this.dateValue, Validators.required],
      notifyTechnician: [false]
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get isSubmitDisabled(): boolean {
    return this.isLoading || this.dateValue === this.initialDateValue || this.rescheduleForm.invalid;
  }

  onDateChange(value: string | undefined): void {
    this.dateValue = value ?? '';
    this.rescheduleForm.patchValue({ date: this.dateValue });
    this.rescheduleForm.get('date')?.markAsTouched();
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  onCancel(): void {
    this.closeModal();
  }

  async onSubmit(): Promise<void> {
    if (this.rescheduleForm.invalid) {
      Object.keys(this.rescheduleForm.controls).forEach(key => {
        this.rescheduleForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isLoading = true;

    const request: RescheduleMaintenanceRequest = {
      maintenanceId: this.maintenanceData.id,
      assetId: this.maintenanceData.assetId,
      dueDate: new Date(this.dateValue),
      notifyTechnician: this.rescheduleForm.value.notifyTechnician
    };

    try {
      await this.maintenanceService.rescheduleMaintenance(request);
      this.toastr.success(this.transloco.translate('maintenanceOverview.toasts.maintenanceRescheduled'));

      this.activeModal.close(true);
      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }
}